import {
  EFFORT_LEVELS,
  isEffortLevel,
  PROVIDERS,
  type EffortLevel,
  type Member,
  type ProviderId,
} from '@ai-studio/shared';

/**
 * Which per-member run modes the member's primary provider can use.
 *
 * Effort is sent only by the Anthropic API adapter and the Claude CLI. Ultracode
 * runs on the API agent loop (spawn_agents) and on the Claude CLI (workflows);
 * the other local CLIs run their own agent loops and have neither.
 */
export interface ModeSupport {
  readonly ok: boolean;
  /** Why the control is disabled. Null when it is usable. */
  readonly reason: string | null;
}

/** Labels in the order the selectors list them. Null is the provider default. */
export const EFFORT_LABELS: Record<EffortLevel, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Extra high',
  max: 'Max',
};

export const EFFORT_OPTIONS: readonly {
  readonly value: EffortLevel | null;
  readonly label: string;
}[] = [
  { value: null, label: 'Default' },
  ...EFFORT_LEVELS.map((value) => ({ value, label: EFFORT_LABELS[value] })),
];

/** Most sub-agents one spawn_agents call may start. Mirrors the worker's cap. */
export const ULTRACODE_MAX_AGENTS = 6;

export const ULTRACODE_LABEL = 'Ultracode (parallel sub-agents)';

export const ULTRACODE_NOTE = `Lets the assistant create and run up to ${ULTRACODE_MAX_AGENTS} sub-agents in parallel for larger jobs, then combine their results. Uses more tokens. Changing it restarts this member's worker, which ends a running turn.`;

export const EFFORT_NOTE =
  'How hard the model thinks on each call. Higher levels are slower and use more tokens; Default leaves it to the model. Models that do not offer a level use the nearest one they do.';

/** Local CLIs that run their own agent loop instead of the worker's. */
const NATIVE_RUNNERS: ReadonlySet<string> = new Set(['claudecli', 'codex', 'antigravity', 'grok']);
const EFFORT_PROVIDERS: ReadonlySet<string> = new Set(['anthropic', 'claudecli']);

function providerName(id: string): string {
  return Object.hasOwn(PROVIDERS, id) ? PROVIDERS[id as ProviderId].label : id;
}

function primaryProvider(member: Pick<Member, 'bindings'>): string | null {
  return member.bindings[0]?.provider ?? null;
}

export function effortSupport(member: Pick<Member, 'bindings'>): ModeSupport {
  const provider = primaryProvider(member);
  if (!provider)
    return { ok: false, reason: 'Set a model on this member in the Team panel first.' };
  if (EFFORT_PROVIDERS.has(provider)) return { ok: true, reason: null };
  return {
    ok: false,
    reason: `Effort works with the Anthropic API and Claude CLI providers. ${providerName(provider)} uses its own default.`,
  };
}

export function ultracodeSupport(member: Pick<Member, 'bindings'>): ModeSupport {
  const provider = primaryProvider(member);
  if (!provider)
    return { ok: false, reason: 'Set a model on this member in the Team panel first.' };
  if (provider === 'claudecli' || !NATIVE_RUNNERS.has(provider)) return { ok: true, reason: null };
  return {
    ok: false,
    reason: `Ultracode is not available for ${providerName(provider)}: that CLI runs its own agent loop. Use an API provider or the Claude CLI.`,
  };
}

/**
 * Why a settings form must disable a mode, or null when it may be set.
 *
 * Unlike the chat controls, a mode can be stored before any model is chosen,
 * so only a provider that cannot use it blocks the setting.
 */
export function blockReason(support: ModeSupport, member: Pick<Member, 'bindings'>): string | null {
  return support.ok || member.bindings.length === 0 ? null : support.reason;
}

/** The select's string value for a stored effort ('' is Default). */
export function effortValue(effort: EffortLevel | null | undefined): string {
  return effort ?? '';
}

/** Parse a select value back into a stored effort. Unknown values mean Default. */
export function parseEffort(value: string): EffortLevel | null {
  return isEffortLevel(value) ? value : null;
}
